/**
 * Currency.ts
 * 
 * Terra Anima DAoC-Style Multi-tier Currency System:
 * 1,000 Copper = 1 Silver
 * 1,000 Silver = 1 Gold (1,000,000 Copper)
 * 1,000 Gold   = 1 Platinum (1,000,000,000 Copper)
 * 
 * Internal base unit: Copper (number)
 */

export interface CurrencyBreakdown {
  platinum: number;
  gold: number;
  silver: number;
  copper: number;
}

export const COPPER_PER_SILVER = 1000;
export const SILVER_PER_GOLD = 1000;
export const GOLD_PER_PLATINUM = 1000;

export const COPPER_PER_GOLD = COPPER_PER_SILVER * SILVER_PER_GOLD; // 1,000,000
export const COPPER_PER_PLATINUM = COPPER_PER_GOLD * GOLD_PER_PLATINUM; // 1,000,000,000

export class Currency {
  /** Convert total copper into platinum, gold, silver, copper parts */
  public static breakdown(totalCopper: number): CurrencyBreakdown {
    let remaining = Math.max(0, Math.floor(totalCopper || 0));
    const platinum = Math.floor(remaining / COPPER_PER_PLATINUM);
    remaining %= COPPER_PER_PLATINUM;

    const gold = Math.floor(remaining / COPPER_PER_GOLD);
    remaining %= COPPER_PER_GOLD;

    const silver = Math.floor(remaining / COPPER_PER_SILVER);
    const copper = remaining % COPPER_PER_SILVER;

    return { platinum, gold, silver, copper };
  }

  /** Calculate total copper from components */
  public static toCopper(breakdown: Partial<CurrencyBreakdown>): number {
    const p = Math.max(0, breakdown.platinum || 0);
    const g = Math.max(0, breakdown.gold || 0);
    const s = Math.max(0, breakdown.silver || 0);
    const c = Math.max(0, breakdown.copper || 0);
    return p * COPPER_PER_PLATINUM + g * COPPER_PER_GOLD + s * COPPER_PER_SILVER + c;
  }

  /**
   * Format currency into readable string, e.g. "🪙10P 🪙789G 🪙456S 🪙123C"
   * compact: if false, always shows all 4 denominations even if 0
   */
  public static format(totalCopper: number, compact: boolean = true): string {
    const { platinum, gold, silver, copper } = this.breakdown(totalCopper);

    if (!compact) {
      return `🪙${platinum.toLocaleString()}P 🪙${gold.toLocaleString()}G 🪙${silver.toLocaleString()}S 🪙${copper.toLocaleString()}C`;
    }

    if (totalCopper <= 0) return '🪙0P 🪙0G 🪙0S 🪙0C';

    const parts: string[] = [];
    if (platinum > 0) parts.push(`🪙${platinum.toLocaleString()}P`);
    if (gold > 0) parts.push(`🪙${gold.toLocaleString()}G`);
    if (silver > 0) parts.push(`🪙${silver.toLocaleString()}S`);
    if (copper > 0 || parts.length === 0) parts.push(`🪙${copper.toLocaleString()}C`);

    return parts.join(' ');
  }

  /**
   * Format with Korean labels: "1플래티넘 250골드 500실버 100코퍼"
   */
  public static formatKorean(totalCopper: number): string {
    const { platinum, gold, silver, copper } = this.breakdown(totalCopper);

    if (totalCopper <= 0) return '0코퍼';

    const parts: string[] = [];
    if (platinum > 0) parts.push(`${platinum.toLocaleString()}플래티넘`);
    if (gold > 0) parts.push(`${gold.toLocaleString()}골드`);
    if (silver > 0) parts.push(`${silver.toLocaleString()}실버`);
    if (copper > 0) parts.push(`${copper.toLocaleString()}코퍼`);

    return parts.join(' ');
  }

  /**
   * Format with icons and colored badges for HTML rendering
   */
  public static formatHtml(totalCopper: number): string {
    const { platinum, gold, silver, copper } = this.breakdown(totalCopper);

    const segments: string[] = [
      `<span style="color:#f97316;font-weight:700;" title="코퍼">🪙${copper.toLocaleString()}<small style="font-size:9px;">C</small></span>`,
      `<span style="color:#cbd5e1;font-weight:700;" title="실버">🪙${silver.toLocaleString()}<small style="font-size:9px;">S</small></span>`,
      `<span style="color:#fde047;font-weight:700;" title="골드">🪙${gold.toLocaleString()}<small style="font-size:9px;">G</small></span>`,
      `<span style="color:#a78bfa;font-weight:700;" title="플래티넘">🪙${platinum.toLocaleString()}<small style="font-size:9px;">P</small></span>`,
    ];

    return segments.join(' ');
  }
}
